/**
 * ProblemResultList 컴포넌트
 * 문제별 채점 결과 목록
 * - 문제 번호, 답형식, 정답/오답 표시
 * - 점수 및 시도 횟수
 */

import React from 'react';
import { PixelCard } from '../common/PixelCard';
import { LatexText } from '../common/LatexText';
import { ProblemResult } from '../../types/result';

/**
 * ProblemResultList Props
 */
interface ProblemResultListProps {
  problems: ProblemResult[];
}

/**
 * ProblemResultList 컴포넌트
 * 문제별 결과를 카드 리스트로 표시
 */
export const ProblemResultList: React.FC<ProblemResultListProps> = ({ problems }) => {
  return (
    <div className="space-y-2">
      {problems.map((problem) => (
        <PixelCard
          key={problem.problem_number}
          variant="panel"
          padding="small"
        >
          <div className="flex justify-between items-center">
            {/* 왼쪽: 문제 번호 */}
            <div>
              <span className="font-pixel text-sm text-white">
                {problem.problem_number}번
              </span>
            </div>

            {/* 오른쪽: 결과 표시 */}
            <div className="flex items-center gap-2">
              {/* 정답 */}
              {problem.is_correct === true && (
                <>
                  <span className="text-green-400 text-lg">✅</span>
                  <div className="text-right">
                    <p className="font-pixel text-xs text-green-400">
                      +{problem.score}점
                    </p>
                    {problem.answer_type !== 'T' && (
                      <p className="font-pixel text-xs text-gray-500">
                        정답: <LatexText text={problem.correct_answer} />
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* 오답 */}
              {problem.is_correct === false && (
                <>
                  <span className="text-red-400 text-lg">❌</span>
                  <div className="text-right">
                    <p className="font-pixel text-xs text-red-400">
                      내 답: <LatexText text={problem.user_answer} />
                    </p>
                  </div>
                </>
              )}

              {/* 채점 대기 (T타입) */}
              {problem.is_correct === null && (
                <>
                  <span className="text-yellow-400 text-lg">⏳</span>
                  <span className="font-pixel text-xs text-yellow-400">
                    채점 대기
                  </span>
                </>
              )}
            </div>
          </div>

          {/* 추가 정보: 시도 횟수 */}
          {problem.attempts > 1 && (
            <div className="mt-2 pt-2 border-t border-gray-700">
              <p className="font-pixel text-xs text-gray-500">
                {problem.attempts}회 시도
              </p>
            </div>
          )}
        </PixelCard>
      ))}

      {/* 문제가 없을 때 */}
      {problems.length === 0 && (
        <div className="text-center py-8">
          <p className="font-pixel text-sm text-gray-500">
            제출한 문제가 없습니다
          </p>
        </div>
      )}
    </div>
  );
};
